import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import Button from '../../components/formElements/Button';
import Loading from '../../components/global/loading/Loading';
import { useOrder } from '../../context/OrderContext';
import { Order } from '../../models/Order';
import { useConfig } from '../../utils/useConfig';

type Props = {
  orderId: string;
};

function OrderConfirmation({ orderId }: Props) {
  const config = useConfig();
  const { getOrder } = useOrder();
  const [order, setOrder] = useState<Order | null | undefined>(null);
  const [countdown, setCountdown] = useState(10);
  const navigate = useNavigate();
  const location = useLocation();

  // Extract query parameters
  const queryParams = new URLSearchParams(location.search);
  const redirect_status = queryParams.get('redirect_status');

  useEffect(() => {
    const loadOrder = async () => {
      try {
        const order = await getOrder(orderId);
        setOrder(order);
      } catch (error: any) {
        setOrder(undefined);
      }
    };

    loadOrder();
  }, [getOrder, orderId]);

  useEffect(() => {
    if (
      redirect_status === 'succeeded' &&
      order !== null &&
      order !== undefined
    ) {
      const timer = setInterval(() => {
        setCountdown((prevCountdown) => {
          if (prevCountdown === 1) {
            clearInterval(timer);
            navigate('/zamówienie/' + orderId);
          }
          return prevCountdown - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [redirect_status, order, orderId, navigate]);

  const renderSuccessMessage = () => (
    <div>
      <h1 className="text-center font-display text-background text-4xl font-semibold">
        Zamówienie zostało opłacone!
      </h1>
      <p className="text-center text-gray-600 mt-4">
        Numer zamówienia: {orderId}
      </p>
      <p className="text-center text-green-600 mt-4">
        Potwierdzenie zostało wysłane na adres email: {order?.email}
      </p>
      <div className="text-center mt-10 font-bold text-4xl flex flex-col items-center">
        <p>Przekierowanie za</p>
        <p className="text-7xl font-bold">{countdown}</p>
        <p>sekund...</p>
      </div>
    </div>
  );

  const renderFailureMessage = () => (
    <div className="max-w-[600px]">
      <h2 className="text-center text-red-600 mt-4 text-3xl">
        Nie udało się opłacić zamówienia
      </h2>
      <p className="text-center text-gray-600 mt-4">
        Sprawdź swój email, aby upewnić się, że zamówienie zostało złożone
        poprawnie. Jeżeli nie otrzymałeś emaila, a opłaciłeś zamówienie,
        skontaktuj się z nami pod adresem:{' '}
        <a href={`mailto:${config.contact.email}`}>${config.contact.email}</a>.
      </p>
      <p className="text-center text-gray-600 mt-4">
        Pamiętaj, aby w wiadomości podać numer zamówienia oraz adres email, na
        który zostało złożone zamówienie.
      </p>
      <Button onClick={() => navigate('/')} className="mt-4" colorTheme="error">
        Powrót na stronę główną
      </Button>
    </div>
  );

  const renderLoadingOrError = () => {
    if (order === null) return <Loading />;
    if (order === undefined) return renderFailureMessage();
    return null;
  };

  return (
    <div className="flex flex-col items-center">
      {redirect_status === 'succeeded' &&
        order !== undefined &&
        renderSuccessMessage()}
      {redirect_status === 'failed' && renderFailureMessage()}
      {redirect_status !== 'succeeded' &&
        redirect_status !== 'failed' &&
        renderLoadingOrError()}
    </div>
  );
}

export default OrderConfirmation;
